import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  AlertCircle,
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Database,
  Inbox,
  Info,
  Package,
  Search,
  X,
  Zap,
} from "lucide-react";
import { BinPickerModal } from "@/components/BinPickerModal";
import {
  FormField,
  MoneyField,
  NumberField,
  DropdownField,
} from "@/components/FormField";
import { PrimaryButton } from "@/components/PrimaryButton";
import { SetCombobox } from "@/components/SetCombobox";
import {
  calcGlobalCostPerCard,
  generateId,
  useApp,
  type Card,
  type CardCondition,
  type CardFinish,
  type CardGame,
  type CardRarity,
} from "@/context/AppContext";
import {
  conditionAdjust,
  getPriceVariants,
  mapFinish,
  mapRarity,
  queryTcgApi,
  searchCards,
  type TcgApiCard,
  type TcgPriceVariant,
} from "@/features/tcgApi";
import {
  invalidateLotCardStats,
  useLotCardStats,
} from "@/features/useLotCardStats";

export const Route = createFileRoute("/inventory/create")({
  component: InventoryCreatePage,
});

const GAMES: CardGame[] = ["Pokemon", "Magic", "Yu-Gi-Oh", "Other"];
const RARITIES: CardRarity[] = [
  "Common",
  "Uncommon",
  "Rare",
  "Promo",
  "Trainer",
  "Energy",
  "Damaged",
  "Sealed",
];
const CONDITIONS: CardCondition[] = ["NM", "LP", "MP", "HP", "Damaged"];
const LISTING_STATUSES = ["Unlisted", "Listed", "Sold"] as const;
type ListingStatus = (typeof LISTING_STATUSES)[number];

// "Regular" = no finish (undefined); the rest map to CardFinish.
const FINISHES = ["Regular", "Holo", "Reverse Holo", "Premium"];
// "English" is the default and stored as undefined (model treats absent = English).
const LANGUAGES = [
  "English",
  "Japanese",
  "Chinese",
  "Korean",
  "German",
  "French",
  "Italian",
  "Spanish",
  "Portuguese",
  "Other",
];
// "Not graded" → no gradingCompany + graded:false; the rest set graded:true.
const GRADING_COMPANIES = [
  "Not graded",
  "PSA",
  "BGS",
  "CGC",
  "SGC",
  "TAG",
  "Other",
];

// Map a TCGplayer price-variant type to our Finish so the two line up: Normal →
// Regular (non-holo), reverse* → Reverse Holo, anything else holo → Holo.
function finishFromVariantType(type: string): string {
  const t = type.toLowerCase();
  if (t.includes("reverse")) return "Reverse Holo";
  if (t.includes("holo")) return "Holo";
  return "Regular";
}

// Auto-pick the most-likely printing for a card: prefer a non-holo variant
// (most Commons/Uncommons are the non-holo print) before falling back to the
// highest-priced variant. Without this, a Common with a rare holofoil price
// would default to Holo just because it costs more.
function pickDefaultVariant<T extends { type: string }>(
  variants: T[],
): T | undefined {
  const nonHolo = variants.find((v) => !v.type.toLowerCase().includes("holo"));
  return nonHolo ?? variants[0];
}

function InventoryCreatePage() {
  const navigate = useNavigate();
  const { lots, addCard } = useApp();

  // Refs for scroll-to-first-error.
  const fieldRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Form fields.
  const [name, setName] = useState("");
  const [game, setGame] = useState<CardGame>("Pokemon");
  const [cardSetName, setCardSetName] = useState("");
  const [setCode, setSetCode] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [rarity, setRarity] = useState<CardRarity>("Rare");
  const [variant, setVariant] = useState("");
  const [finish, setFinish] = useState("Regular");
  const [language, setLanguage] = useState("English");
  const [gradingCompany, setGradingCompany] = useState("Not graded");
  const [condition, setCondition] = useState<CardCondition>("NM");
  const [quantity, setQuantity] = useState("1");
  const [sourceLotId, setSourceLotId] = useState("");
  const [costBasis, setCostBasis] = useState("");
  const [marketPrice, setMarketPrice] = useState("");
  const [expectedSalePrice, setExpectedSalePrice] = useState("");
  const [storageLocation, setStorageLocation] = useState("");
  const [binPickerVisible, setBinPickerVisible] = useState(false);
  const [listingStatus, setListingStatus] = useState<ListingStatus>("Unlisted");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  // Field errors.
  const [nameError, setNameError] = useState("");
  const [conditionError, setConditionError] = useState("");
  const [quantityError, setQuantityError] = useState("");
  const [costBasisError, setCostBasisError] = useState("");
  const [marketPriceError, setMarketPriceError] = useState("");
  const [expectedSalePriceError, setExpectedSalePriceError] = useState("");

  // Auto-fill tracking.
  const [costAutoFilled, setCostAutoFilled] = useState(false);
  const [costGlobalFilled, setCostGlobalFilled] = useState(false);

  // Lookup state.
  const [lookupOpen, setLookupOpen] = useState(false);
  const [lookupQuery, setLookupQuery] = useState("");
  const [lookupResults, setLookupResults] = useState<TcgApiCard[]>([]);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const [selectedCard, setSelectedCard] = useState<TcgApiCard | null>(null);
  const [priceVariants, setPriceVariants] = useState<TcgPriceVariant[]>([]);
  const [selectedVariant, setSelectedVariant] =
    useState<TcgPriceVariant | null>(null);
  const [lookupNmPrice, setLookupNmPrice] = useState<number | null>(null);

  // Set + Card Number "Find card" lookup (pokemontcg.io set id from the combobox).
  const [setApiId, setSetApiId] = useState("");
  const [findLoading, setFindLoading] = useState(false);
  const [findError, setFindError] = useState("");

  const lotOptions = lots.map((l) => l.id);
  // {value,label} options for the Source Lot dropdown — newest first, labelled
  // "Seller · date" (with a "— No lot —" entry), instead of raw lot ids.
  const lotSelectOptions = useMemo(
    () => [
      { value: "", label: "— No lot —" },
      ...[...lots]
        .sort(
          (a, b) =>
            new Date(b.purchaseDate).getTime() -
            new Date(a.purchaseDate).getTime(),
        )
        .map((l) => {
          const date = new Date(l.purchaseDate).toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
          });
          return {
            value: l.id,
            label: l.seller ? `${l.seller} · ${date}` : date,
          };
        }),
    ],
    [lots],
  );

  // Server-side singles roll-up (TNK-155 phase E) — `cards` is no longer in
  // memory at this point, so the count + cost-allocated come from the
  // /lots/:id/card-stats endpoint via the hook.
  const { stats: lotCardStats } = useLotCardStats(sourceLotId || null);

  // Lot stats (prefer sorted-bin total, else estimated). Skeletons the
  // singles fields to zero until the per-lot fetch resolves.
  const getLotStats = (lotId: string) => {
    const lot = lots.find((l) => l.id === lotId);
    if (!lot) return null;
    const totalCost = lot.purchaseCost + lot.shippingCost + lot.taxFees;
    const sortedCount = lot.sortingBins.reduce((s, b) => s + (b.count || 0), 0);
    const totalCards = sortedCount > 0 ? sortedCount : lot.estimatedCards;
    const singlesCount = lotCardStats?.singlesCount ?? 0;
    const costAllocated = lotCardStats?.costAllocated ?? 0;
    const perCardCost = totalCards > 0 ? totalCost / totalCards : 0;
    return {
      lot,
      totalCost,
      totalCards,
      singlesCount,
      costAllocated,
      perCardCost,
    };
  };

  const lotStats = sourceLotId ? getLotStats(sourceLotId) : null;
  const globalCPC = useMemo(() => calcGlobalCostPerCard(lots), [lots]);

  // Auto-fill cost basis when sourceLotId changes.
  useEffect(() => {
    if (!sourceLotId) {
      if (costAutoFilled || costGlobalFilled || !costBasis.trim()) {
        setCostAutoFilled(false);
        if (globalCPC > 0) {
          setCostBasis(globalCPC.toFixed(4));
          setCostGlobalFilled(true);
        } else {
          setCostBasis("");
          setCostGlobalFilled(false);
        }
      }
      return;
    }
    setCostGlobalFilled(false);
    const stats = getLotStats(sourceLotId);
    if (!stats || stats.perCardCost <= 0) return;
    if (!costBasis || costAutoFilled || costGlobalFilled) {
      setCostBasis(stats.perCardCost.toFixed(4));
      setCostAutoFilled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceLotId]);

  // On mount: fill cost basis with global average if no lot and field empty.
  useEffect(() => {
    if (!sourceLotId && globalCPC > 0 && !costBasis) {
      setCostBasis(globalCPC.toFixed(4));
      setCostGlobalFilled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recalculate expected sale price when condition changes (if from lookup).
  useEffect(() => {
    if (lookupNmPrice == null) return;
    const adjusted = conditionAdjust(lookupNmPrice, condition);
    setExpectedSalePrice(adjusted.toFixed(2));
  }, [condition, lookupNmPrice]);

  const handleSearch = async () => {
    const q = lookupQuery.trim();
    if (!q) return;
    setLookupLoading(true);
    setLookupError("");
    setLookupResults([]);
    try {
      const results = await searchCards(q);
      if (results.length > 0) {
        setLookupResults(results);
      } else {
        setLookupError(
          `No results for "${q}". Try a shorter or different name.`,
        );
      }
    } catch {
      setLookupError("Could not reach TCG API. Check your connection.");
    } finally {
      setLookupLoading(false);
    }
  };

  const handleSelectCard = (card: TcgApiCard) => {
    setSelectedCard(card);
    const variants = getPriceVariants(card);
    setPriceVariants(variants);
    setName(card.name);
    setCardSetName(card.set.name);
    setSetCode(card.set.id.toUpperCase());
    setCardNumber(card.number);
    setRarity(mapRarity(card.rarity, card.supertype, card.subtypes, card.hp));
    setFinish(
      mapFinish(card.rarity, card.supertype, card.subtypes, card.hp) ??
        "Regular",
    );
    const defaultVariant = pickDefaultVariant(variants);
    if (defaultVariant) {
      applyVariant(defaultVariant);
    }
    setLookupResults([]);
  };

  const applyVariant = (v: TcgPriceVariant) => {
    setSelectedVariant(v);
    setLookupNmPrice(v.market);
    setMarketPrice(v.market.toFixed(2));
    setExpectedSalePrice(conditionAdjust(v.market, condition).toFixed(2));
    setVariant(v.label);
    // Keep Finish aligned with the chosen printing (don't downgrade a
    // rarity-derived Premium, which has no TCGplayer price type).
    setFinish((prev) =>
      prev === "Premium" ? prev : finishFromVariantType(v.type),
    );
  };

  // Fill the card-specific fields from a resolved catalog card, leaving the
  // operator's chosen Set + Card Number intact (used by the Find-card lookup).
  const applyResolvedCard = (card: TcgApiCard) => {
    setSelectedCard(card);
    const variants = getPriceVariants(card);
    setPriceVariants(variants);
    setName(card.name);
    setRarity(mapRarity(card.rarity, card.supertype, card.subtypes, card.hp));
    setFinish(
      mapFinish(card.rarity, card.supertype, card.subtypes, card.hp) ??
        "Regular",
    );
    const defaultVariant = pickDefaultVariant(variants);
    if (defaultVariant) applyVariant(defaultVariant);
  };

  // Look up the exact card by the picked set + entered number and auto-fill.
  const findBySetNumber = async () => {
    // Take the part before any "/total" suffix; drop leading zeros on a purely
    // numeric value so "025" matches the catalog's "25". A slash left in the
    // query is a Lucene regex delimiter and 400s, hence the strip.
    const raw = cardNumber.split("/")[0].trim();
    const num = /^\d+$/.test(raw) ? raw.replace(/^0+(?=\d)/, "") : raw;
    if (!setApiId || !num) return;
    setFindLoading(true);
    setFindError("");
    try {
      const results = await queryTcgApi(`set.id:${setApiId} number:"${num}"`);
      const match =
        results.find((c) => c.number.toLowerCase() === num.toLowerCase()) ??
        results[0];
      if (match) applyResolvedCard(match);
      else setFindError(`No card found for #${num} in that set.`);
    } catch {
      setFindError(`Lookup failed for #${num} — please try again.`);
    } finally {
      setFindLoading(false);
    }
  };

  const clearLookup = () => {
    setSelectedCard(null);
    setPriceVariants([]);
    setSelectedVariant(null);
    setLookupNmPrice(null);
    setLookupResults([]);
    setLookupError("");
  };

  const scrollToFirstError = (errorOrder: string[]) => {
    for (const key of errorOrder) {
      const el = fieldRefs.current[key];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
    }
  };

  const handleSave = () => {
    const errMap: Record<string, boolean> = {};
    if (!name.trim()) {
      setNameError("Enter a card name");
      errMap.name = true;
    } else setNameError("");
    if (!condition) {
      setConditionError("Select a condition");
      errMap.condition = true;
    } else setConditionError("");
    const qty = parseInt(quantity, 10);
    if (!quantity.trim() || isNaN(qty) || qty < 1) {
      setQuantityError("Enter a whole number of 1 or more");
      errMap.quantity = true;
    } else setQuantityError("");
    if (
      costBasis.trim() &&
      (isNaN(parseFloat(costBasis)) || parseFloat(costBasis) < 0)
    ) {
      setCostBasisError("Enter a valid non-negative amount");
      errMap.costBasis = true;
    } else setCostBasisError("");
    if (
      marketPrice.trim() &&
      (isNaN(parseFloat(marketPrice)) || parseFloat(marketPrice) < 0)
    ) {
      setMarketPriceError("Enter a valid non-negative amount");
      errMap.marketPrice = true;
    } else setMarketPriceError("");
    if (
      expectedSalePrice.trim() &&
      (isNaN(parseFloat(expectedSalePrice)) ||
        parseFloat(expectedSalePrice) < 0)
    ) {
      setExpectedSalePriceError("Enter a valid non-negative amount");
      errMap.expectedSalePrice = true;
    } else setExpectedSalePriceError("");

    if (Object.keys(errMap).length > 0) {
      scrollToFirstError(Object.keys(errMap));
      return;
    }

    setSaving(true);
    const card: Card = {
      id: generateId("SKU"),
      name: name.trim(),
      game,
      setName: cardSetName,
      setCode,
      cardNumber,
      rarity,
      variant,
      condition,
      quantity: parseInt(quantity, 10) || 1,
      sourceLotId,
      costBasis: parseFloat(costBasis) || 0,
      marketPrice: parseFloat(marketPrice) || 0,
      expectedSalePrice: parseFloat(expectedSalePrice) || 0,
      storageLocation,
      listingStatus,
      saleVenue: "",
      notes,
      createdAt: new Date().toISOString(),
      finish: finish === "Regular" ? undefined : (finish as CardFinish),
      language: language === "English" ? undefined : language,
      graded: gradingCompany !== "Not graded",
      gradingCompany:
        gradingCompany === "Not graded" ? undefined : gradingCompany,
      ...(selectedCard && {
        tcgSupertype: selectedCard.supertype,
        tcgSubtypes: selectedCard.subtypes,
        tcgTypes: selectedCard.types,
        tcgHp: selectedCard.hp,
        tcgArtist: selectedCard.artist,
        tcgPokedexNumbers: selectedCard.nationalPokedexNumbers,
        tcgSeries: selectedCard.set?.series,
      }),
    };
    addCard(card);
    // Bust the per-lot singles cache so the next visit to a lot-stats
    // dropdown reflects this new row.
    if (sourceLotId) invalidateLotCardStats(sourceLotId);
    setSaving(false);
    navigate({ to: "/inventory" });
  };

  const hasFilled = selectedCard != null;

  // Field-ref binders.
  const bindField = (key: string) => (el: HTMLDivElement | null) => {
    fieldRefs.current[key] = el;
  };

  return (
    <div className="space-y-3 pb-12">
      <Link
        to="/inventory"
        className="-ml-1 inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-5 w-5" />
        Cancel
      </Link>

      <h1 className="text-[22px] font-bold text-foreground">
        Add Card to Inventory
      </h1>

      {/* TCG Lookup */}
      <div
        className={`overflow-hidden rounded-md border bg-card ${
          hasFilled ? "border-secondary/60" : "border-border"
        }`}
      >
        <button
          type="button"
          onClick={() => setLookupOpen((o) => !o)}
          className="flex w-full items-center justify-between gap-2 p-3.5 text-left"
        >
          <div className="flex flex-1 items-center gap-2">
            <Database
              className={`h-4 w-4 ${hasFilled ? "text-secondary-foreground" : "text-primary"}`}
            />
            <span
              className={`text-sm font-semibold ${hasFilled ? "text-secondary-foreground" : "text-primary"}`}
            >
              {hasFilled
                ? `Auto-filled: ${selectedCard!.name}`
                : "Lookup from TCG Database"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {hasFilled && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  clearLookup();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    clearLookup();
                  }
                }}
                className="cursor-pointer rounded bg-border px-2 py-1 text-[11px] font-medium text-muted-foreground hover:bg-border/80"
              >
                Clear
              </span>
            )}
            {lookupOpen ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
        </button>

        {hasFilled && !lookupOpen && (
          <div className="border-t border-border px-3.5 pb-3 pt-2">
            <div className="text-xs text-muted-foreground">
              {selectedCard!.set.name} · #{selectedCard!.number}
              {selectedCard!.rarity ? ` · ${selectedCard!.rarity}` : ""}
            </div>
            {selectedVariant && (
              <div className="text-xs text-muted-foreground">
                TCGplayer NM ({selectedVariant.label}):{" "}
                <span className="font-semibold text-secondary-foreground">
                  ${selectedVariant.market.toFixed(2)}
                </span>
              </div>
            )}
          </div>
        )}

        {lookupOpen && (
          <div className="space-y-2.5 border-t border-border p-3.5">
            <div className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2.5">
              <Search className="h-4 w-4 text-muted-foreground" />
              <input
                type="search"
                value={lookupQuery}
                onChange={(e) => setLookupQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
                placeholder="Charizard, Mr. Mime, Ho-Oh…"
                autoCapitalize="words"
                autoComplete="off"
                autoCorrect="off"
                className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
              />
              {lookupQuery.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setLookupQuery("");
                    setLookupResults([]);
                    setLookupError("");
                  }}
                  className="text-muted-foreground hover:text-foreground"
                  aria-label="Clear"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={handleSearch}
              disabled={lookupLoading || !lookupQuery.trim()}
              className="flex w-full items-center justify-center gap-2 rounded-md bg-primary py-2.5 text-[13px] font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {lookupLoading ? (
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
              ) : (
                <>
                  <Search className="h-3.5 w-3.5" />
                  Search
                </>
              )}
            </button>

            {lookupError && (
              <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-2.5 text-xs text-destructive">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                <span>{lookupError}</span>
              </div>
            )}

            {lookupResults.map((card) => {
              const variants = getPriceVariants(card);
              const best = variants[0];
              return (
                <button
                  key={card.id}
                  type="button"
                  onClick={() => handleSelectCard(card)}
                  className="flex w-full items-center gap-2 rounded-md border border-border p-3 text-left hover:bg-muted/40"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-foreground">
                      {card.name}
                    </div>
                    <div className="truncate text-[11px] text-muted-foreground">
                      {card.set.name} · #{card.number}
                      {card.rarity ? ` · ${card.rarity}` : ""}
                    </div>
                  </div>
                  {best ? (
                    <div className="flex flex-col items-end gap-0.5">
                      <div className="text-[15px] font-bold text-secondary-foreground">
                        ${best.market.toFixed(2)}
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        {best.label}
                      </div>
                    </div>
                  ) : (
                    <span className="text-[11px] text-muted-foreground">
                      No price
                    </span>
                  )}
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </button>
              );
            })}

            {selectedCard && priceVariants.length > 0 && (
              <div className="space-y-2.5">
                <div className="text-[10px] font-medium tracking-wider text-muted-foreground">
                  SELECT PRICE VARIANT
                </div>
                <div className="flex flex-wrap gap-2">
                  {priceVariants.map((v) => {
                    const isSelected = selectedVariant?.type === v.type;
                    return (
                      <button
                        key={v.type}
                        type="button"
                        onClick={() => applyVariant(v)}
                        className={`flex min-w-[44%] flex-1 flex-col gap-0.5 rounded-md border p-2.5 text-left ${
                          isSelected
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border bg-background"
                        }`}
                      >
                        <span
                          className={`text-[10px] font-medium tracking-wide ${isSelected ? "text-primary-foreground/60" : "text-muted-foreground"}`}
                        >
                          {v.label}
                        </span>
                        <span
                          className={`text-[17px] font-bold ${isSelected ? "text-emerald-300" : "text-foreground"}`}
                        >
                          ${v.market.toFixed(2)}
                        </span>
                        {v.low != null && v.high != null && (
                          <span
                            className={`text-[10px] ${isSelected ? "text-primary-foreground/40" : "text-muted-foreground"}`}
                          >
                            ${v.low.toFixed(2)}–${v.high.toFixed(2)}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {selectedVariant && (
                  <div className="flex items-start gap-2 rounded-md bg-secondary/10 p-2.5 text-xs">
                    <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-secondary-foreground" />
                    <span className="text-secondary-foreground">
                      Expected price auto-adjusts when you change condition
                      below.
                    </span>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => setLookupOpen(false)}
                  className="flex w-full items-center justify-center gap-2 rounded-md bg-secondary py-3 text-[13px] font-bold text-secondary-foreground hover:opacity-90"
                >
                  Done — Apply to Form
                </button>
              </div>
            )}

            {lookupResults.length === 0 &&
              !lookupLoading &&
              !lookupError &&
              !selectedCard && (
                <p className="text-xs leading-5 text-muted-foreground">
                  Fills card name, set, number, rarity, and market price
                  automatically.
                  <br />
                  Handles special names: Mr. Mime, Ho-Oh, Farfetch&apos;d.
                </p>
              )}
          </div>
        )}
      </div>

      {/* Card details */}
      <div className="space-y-1 rounded-md bg-card p-4">
        <div className="mb-1 text-[10px] font-medium tracking-wider text-muted-foreground">
          CARD DETAILS
        </div>
        <div ref={bindField("name")}>
          <FormField
            label="Card Name"
            value={name}
            onChangeText={(v) => {
              setName(v);
              if (v.trim()) setNameError("");
            }}
            placeholder="e.g. Charizard"
            required
            error={nameError}
          />
        </div>
        <DropdownField
          label="Game"
          value={game}
          options={GAMES}
          onChange={(v) => setGame(v as CardGame)}
        />
        {game === "Pokemon" ? (
          <SetCombobox
            value={cardSetName}
            onChange={(name) => {
              setCardSetName(name);
              setSetApiId("");
            }}
            onSelectSet={(name, code, id) => {
              setCardSetName(name);
              if (code) setSetCode(code);
              setSetApiId(id);
            }}
          />
        ) : (
          <FormField
            label="Set Name"
            value={cardSetName}
            onChangeText={setCardSetName}
            placeholder="e.g. Base Set"
          />
        )}
        <FormField
          label="Set Code"
          value={setCode}
          onChangeText={setSetCode}
          placeholder="e.g. BS1"
        />
        <FormField
          label="Card Number"
          value={cardNumber}
          onChangeText={setCardNumber}
          placeholder="e.g. 4/102"
        />
        {game === "Pokemon" && (
          <div className="-mt-2 mb-4 space-y-1">
            <button
              type="button"
              onClick={findBySetNumber}
              disabled={!setApiId || !cardNumber.trim() || findLoading}
              className="inline-flex items-center gap-1.5 rounded-md border border-secondary/40 bg-secondary/20 px-3 py-2 text-xs font-semibold text-secondary-foreground hover:bg-secondary/30 disabled:opacity-50"
            >
              <Search className="h-3.5 w-3.5" />
              {findLoading ? "Finding…" : "Find card from set + number"}
            </button>
            {!setApiId && cardNumber.trim() && (
              <p className="text-[11px] text-muted-foreground">
                Pick a set from the list above to enable lookup.
              </p>
            )}
            {findError && (
              <p className="text-[11px] text-destructive">{findError}</p>
            )}
          </div>
        )}
        <DropdownField
          label="Rarity"
          value={rarity}
          options={RARITIES}
          onChange={(v) => setRarity(v as CardRarity)}
        />
        <DropdownField
          label="Finish"
          value={finish}
          options={FINISHES}
          onChange={setFinish}
        />
        <FormField
          label="Variant"
          value={variant}
          onChangeText={setVariant}
          placeholder="1st ed, staff, misprint…"
        />
        <DropdownField
          label="Language"
          value={language}
          options={LANGUAGES}
          onChange={setLanguage}
        />
        <div ref={bindField("condition")}>
          <DropdownField
            label="Condition"
            value={condition}
            options={CONDITIONS}
            onChange={(v) => {
              setCondition(v as CardCondition);
              setConditionError("");
            }}
            required
            error={conditionError}
          />
        </div>
        <DropdownField
          label="Grading"
          value={gradingCompany}
          options={GRADING_COMPANIES}
          onChange={setGradingCompany}
        />
        <div ref={bindField("quantity")}>
          <NumberField
            label="Quantity"
            value={quantity}
            onChangeText={(v) => {
              setQuantity(v);
              if (v.trim() && parseInt(v, 10) >= 1) setQuantityError("");
            }}
            placeholder="1"
            error={quantityError}
          />
        </div>
      </div>

      {/* Pricing */}
      <div className="space-y-1 rounded-md bg-card p-4">
        <div className="mb-1 text-[10px] font-medium tracking-wider text-muted-foreground">
          PRICING
        </div>

        {lotOptions.length > 0 && (
          <DropdownField
            label="Source Lot"
            value={sourceLotId}
            options={lotSelectOptions}
            onChange={(v) => setSourceLotId(v)}
          />
        )}

        {lotStats && (
          <div className="my-2 overflow-hidden rounded-md border border-primary/30 bg-primary/5">
            <div className="flex items-center gap-2 px-3 py-2.5">
              <Package className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs font-semibold text-primary">
                {lotStats.lot.seller || "Lot"} · Cost Breakdown
              </span>
            </div>
            <div className="flex gap-3 px-3 pb-3">
              <div className="flex-1 space-y-0.5">
                <div className="text-[10px] font-medium tracking-wide text-muted-foreground">
                  Total Lot Cost
                </div>
                <div className="text-lg font-bold text-foreground">
                  ${lotStats.totalCost.toFixed(2)}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  purchase + shipping + tax
                </div>
              </div>
              <div className="w-px self-stretch bg-border" />
              <div className="flex-1 space-y-0.5">
                <div className="text-[10px] font-medium tracking-wide text-muted-foreground">
                  Cards in Lot
                </div>
                <div className="text-lg font-bold text-foreground">
                  {lotStats.totalCards.toLocaleString()}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {lotStats.lot.sortingBins.reduce(
                    (s, b) => s + (b.count || 0),
                    0,
                  ) > 0
                    ? "from sorted bins"
                    : "estimated"}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 border-t border-border px-3 py-2.5">
              <div className="flex-1">
                <div className="text-xs font-semibold text-muted-foreground">
                  Per-card cost basis
                </div>
                <div className="text-[11px] text-muted-foreground">
                  ${lotStats.totalCost.toFixed(2)} ÷ {lotStats.totalCards} cards
                </div>
              </div>
              <div className="text-xl font-bold text-secondary-foreground">
                ${lotStats.perCardCost.toFixed(4)}
              </div>
            </div>
            {lotStats.singlesCount > 0 && (
              <div className="border-t border-border px-3 py-2 text-[11px] text-muted-foreground">
                {lotStats.singlesCount} singles already pulled · $
                {lotStats.costAllocated.toFixed(2)} allocated
              </div>
            )}
          </div>
        )}

        <div ref={bindField("costBasis")}>
          <MoneyField
            label={
              costAutoFilled
                ? "Cost Basis (auto from lot)"
                : costGlobalFilled
                  ? "Cost Basis (portfolio avg)"
                  : "Cost Basis"
            }
            value={costBasis}
            onChangeText={(v) => {
              setCostBasis(v);
              setCostAutoFilled(false);
              setCostGlobalFilled(false);
              if (!v.trim() || (!isNaN(parseFloat(v)) && parseFloat(v) >= 0))
                setCostBasisError("");
            }}
            error={costBasisError}
          />
          {costGlobalFilled && (
            <p className="mt-1 px-0.5 text-[11px] text-muted-foreground">
              Using portfolio average · ${globalCPC.toFixed(4)}/card across all
              lots
            </p>
          )}
        </div>

        {hasFilled && lookupNmPrice != null && (
          <div className="flex items-center gap-2 rounded-md bg-secondary/10 p-2.5 text-xs">
            <Zap className="h-3.5 w-3.5 shrink-0 text-secondary-foreground" />
            <span className="text-secondary-foreground">
              TCGplayer NM: ${lookupNmPrice.toFixed(2)} · {condition} adjusted:
              ${conditionAdjust(lookupNmPrice, condition).toFixed(2)}
            </span>
          </div>
        )}

        <div ref={bindField("marketPrice")}>
          <MoneyField
            label="Market Price (NM)"
            value={marketPrice}
            onChangeText={(v) => {
              setMarketPrice(v);
              if (!v.trim() || (!isNaN(parseFloat(v)) && parseFloat(v) >= 0))
                setMarketPriceError("");
            }}
            error={marketPriceError}
          />
        </div>

        <div ref={bindField("expectedSalePrice")}>
          <MoneyField
            label="Expected Sale Price"
            value={expectedSalePrice}
            onChangeText={(v) => {
              setExpectedSalePrice(v);
              if (!v.trim() || (!isNaN(parseFloat(v)) && parseFloat(v) >= 0))
                setExpectedSalePriceError("");
            }}
            error={expectedSalePriceError}
          />
        </div>
      </div>

      {/* Storage & status */}
      <div className="space-y-3 rounded-md bg-card p-4">
        <div className="text-[10px] font-medium tracking-wider text-muted-foreground">
          STORAGE & STATUS
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Storage Bin
          </label>
          <button
            type="button"
            onClick={() => setBinPickerVisible(true)}
            className="flex w-full items-center gap-2 rounded-md bg-muted px-3 py-3 text-left"
          >
            <Inbox
              className={`h-3.5 w-3.5 ${storageLocation ? "text-secondary-foreground" : "text-muted-foreground"}`}
            />
            <span
              className={`flex-1 text-sm ${storageLocation ? "font-medium text-foreground" : "text-muted-foreground"}`}
            >
              {storageLocation || "Tap to assign a bin…"}
            </span>
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        </div>

        <DropdownField
          label="Listing Status"
          value={listingStatus}
          options={LISTING_STATUSES as unknown as string[]}
          onChange={(v) => setListingStatus(v as ListingStatus)}
        />
        <FormField
          label="Notes"
          value={notes}
          onChangeText={setNotes}
          placeholder="Any additional details…"
          multiline
        />
      </div>

      <PrimaryButton
        label="Add Card to Inventory"
        onClick={handleSave}
        loading={saving}
      />

      <BinPickerModal
        open={binPickerVisible}
        value={storageLocation}
        onSelect={(n) => setStorageLocation(n)}
        onClose={() => setBinPickerVisible(false)}
      />
    </div>
  );
}
